import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { AuthClient } from '@icp-sdk/auth/client';
import type { Identity } from '@icp-sdk/core/agent';
import { principal, explain } from './api';
import { backend } from './backend';
import { recordAnalyticsAccount } from './site-analytics';

interface AuthState {
  owner: string | null; ready: boolean; busy: boolean; error: string;
  favourites: Set<string>; favouritesLoading: boolean; pending: Set<string>;
  login: () => Promise<void>; logout: () => Promise<void>; toggle: (id: string) => Promise<void>;
  clearError: () => void;
  getIdentity: () => Promise<Identity>;
}
const Context = createContext<AuthState | null>(null);
export function AuthProvider({ children }: { children: ReactNode }) {
  const client = useRef<AuthClient | null>(null);
  const activeOwner = useRef<string | null>(null);
  const [owner, setOwner] = useState<string | null>(null);
  const [ready, setReady] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [saved, setSaved] = useState<{ owner: string; tokens: Set<string> } | null>(null);
  const [favouritesLoading, setFavouritesLoading] = useState(false);
  const [pending, setPending] = useState(new Set<string>());
  const inflight = useRef(new Set<string>());
  useEffect(() => {
    let auth: AuthClient;
    try {
      const local = import.meta.env.VITE_DFX_NETWORK === 'local';
      const iiId = import.meta.env.VITE_II_CANISTER_ID;
      const iiFrontendId = import.meta.env.VITE_II_FRONTEND_CANISTER_ID;
      if (local) {
        const host = new URL(import.meta.env.VITE_DFX_HOST);
        auth = new AuthClient({ identityProvider: { authorizeUrl: `http://${iiFrontendId || iiId}.localhost:${host.port || '4943'}/authorize`, canisterId: iiId }, agentOptions: { host: import.meta.env.VITE_DFX_HOST, shouldFetchRootKey: true } });
      } else {
        let frontendId: string;
        try {
          frontendId = principal(import.meta.env.VITE_FRONTEND_CANISTER_ID).toText();
          if (!frontendId.endsWith('-cai')) throw Error('Invalid frontend canister ID');
        } catch {
          setError('Sign-in is unavailable because this deployment is missing a valid frontend canister ID.');
          setReady(true);
          return;
        }
        // Keep accounts stable when this app is opened through its custom domain.
        // The frontend canister certifies the allowed domains in ii-alternative-origins.
        const derivationOrigin = `https://${frontendId}.icp0.io`;
        const gatewayOrigins = ['icp0.io', 'ic0.app', 'icp.net'].map(domain => `https://${frontendId}.${domain}`);
        auth = new AuthClient(gatewayOrigins.includes(window.location.origin) ? {} : { derivationOrigin });
      }
      client.current = auth;
    }
    catch { setError('Enable browser storage to sign in with Internet Identity.'); setReady(true); return; }
    let active = true;
    const restore = async () => {
      try {
        const identity = auth.isAuthenticated() ? await auth.getIdentity() : null;
        if (active) { const next = identity?.getPrincipal().toText() ?? null; activeOwner.current = next; setOwner(next); if (identity) void recordAnalyticsAccount(identity); }
      } catch { if (active) { activeOwner.current = null; setOwner(null); } }
      finally { if (active) setReady(true); }
    };
    void restore(); const unsubscribe = auth.subscribe(restore);
    return () => { active = false; unsubscribe(); auth.dispose(); client.current = null; };
  }, []);
  useEffect(() => {
    let active = true;
    setSaved(null); setPending(new Set()); inflight.current.clear();
    if (!owner) { setFavouritesLoading(false); return; }
    setFavouritesLoading(true);
    void client.current!.getIdentity().then(identity => {
      if (identity.getPrincipal().toText() !== owner) throw Error('Your sign-in changed. Reload to load this account’s favourites.');
      return backend(identity);
    }).then(actor => actor.favourites()).then(value => {
      if (active) setSaved({ owner, tokens: new Set(value.map(token => token.toText())) });
    }).catch(reason => { if (active) setError('Could not load favourites. ' + explain(reason)); }).finally(() => { if (active) setFavouritesLoading(false); });
    return () => { active = false; };
  }, [owner]);
  async function login() {
    setBusy(true); setError('');
    try {
      if (!client.current) throw Error('Sign-in is unavailable. Enable browser storage and reload.');
      const identity = await client.current.signIn();
      const next = identity.getPrincipal().toText(); activeOwner.current = next; setOwner(next); void recordAnalyticsAccount(identity);
    } catch (reason) { setError(explain(reason)); }
    finally { setBusy(false); }
  }
  async function logout() {
    setBusy(true); setError('');
    try { await client.current?.signOut(); activeOwner.current = null; setOwner(null); setSaved(null); }
    catch (reason) { setError(explain(reason)); }
    finally { setBusy(false); }
  }
  async function toggle(id: string) {
    if (!owner) { await login(); return; }
    if (inflight.current.has(id) || favouritesLoading || saved?.owner !== owner) return;
    inflight.current.add(id); setPending(new Set(inflight.current)); setError('');
    const capturedOwner = owner, add = !saved.tokens.has(id);
    try {
      const identity: Identity | undefined = await client.current?.getIdentity();
      if (!identity || identity.getPrincipal().toText() !== capturedOwner || activeOwner.current !== capturedOwner) throw Error('Your session changed. Sign in again.');
      const result = await (await backend(identity)).set_favourite(principal(id), add);
      if ('err' in result) throw Error(result.err);
      if (activeOwner.current === capturedOwner) setSaved(old => {
        if (old?.owner !== capturedOwner) return old;
        const tokens = new Set(old.tokens); if (add) tokens.add(id); else tokens.delete(id);
        return { owner: capturedOwner, tokens };
      });
    } catch (reason) { if (activeOwner.current === capturedOwner) setError(explain(reason)); }
    finally { inflight.current.delete(id); setPending(new Set(inflight.current)); }
  }
  async function getIdentity() {
    const identity = await client.current?.getIdentity();
    if (!identity || !owner || identity.getPrincipal().toText() !== owner || activeOwner.current !== owner) throw Error('Your session changed. Sign in again to continue.');
    return identity;
  }
  return <Context.Provider value={{ owner, ready, busy, error, favourites: saved?.owner === owner ? saved.tokens : new Set(), favouritesLoading, pending, login, logout, toggle, getIdentity, clearError: () => setError('') }}>{children}</Context.Provider>;
}
export function useAuth() { const value = useContext(Context); if (!value) throw Error('AuthProvider is missing'); return value; }
